import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import PlaceInput from "../components/PlaceInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  Plus,
  X,
  Loader2,
  Ship,
  MapPin,
  Calendar,
  Package,
  ArrowRight,
  CheckCircle,
  Clock,
  DollarSign,
  RefreshCw,
  Mail,
  ExternalLink,
  AlertCircle,
  FileText,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL } from "../config/api";


// Carrier logos and colors
const CARRIERS = {
  maersk: { name: "Maersk", color: "#00A3E0", logo: "🚢" },
  msc: { name: "MSC", color: "#FDB913", logo: "🚢" },
  cma: { name: "CMA CGM", color: "#E30613", logo: "🚢" },
  hapag: { name: "Hapag-Lloyd", color: "#FF6600", logo: "🚢" },
};

export default function RateFinderPage() {
  const { token, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState(null);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("search");
  const navigate = useNavigate();
  
  // Search form state - Hapag-Lloyd style
  const [formData, setFormData] = useState({
    origin: "",
    origin_type: "terminal", // terminal or door
    destination: "",
    destination_type: "terminal",
    valid_from: new Date().toISOString().split("T")[0],
    container_type: "40HC",
    container_quantity: 1,
    weight_per_container: 20000,
    commodity: "FAK",
    dangerous_goods: false,
    shipper_owned: false,
  });
  
  // Multi-routing support
  const [routes, setRoutes] = useState([{ origin: "", destination: "" }]);
  
  // Search history
  const [searchHistory, setSearchHistory] = useState([]);

  useEffect(() => {
    fetchSearchHistory();
  }, []);

  const fetchSearchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/rate-finder/history`, {
        headers: getAuthHeaders(),
      });
      setSearchHistory(response.data.searches || []);
    } catch (error) {
      console.error("Failed to fetch history", error);
    }
  };

  const addRoute = () => {
    setRoutes([...routes, { origin: "", destination: "" }]);
  };

  const removeRoute = (index) => {
    if (routes.length > 1) {
      setRoutes(routes.filter((_, i) => i !== index));
    }
  };

  // Hand a found rate off to the Offer Builder (prefills a standalone FCL offer).
  const buildOfferFromRate = (rate) => {
    const pol = results?.search?.origin || routes[0]?.origin || "";
    const pod = results?.search?.destination || routes[0]?.destination || "";
    const payload = {
      pol, pod,
      carrier: CARRIERS[rate.carrier]?.name || rate.carrier,
      rate: rate.rate,
      currency: rate.currency || "USD",
      container_type: formData.container_type,
      container_quantity: formData.container_quantity,
      transit_days: rate.transit_days,
      valid_until: rate.valid_until,
      included_charges: (rate.included_charges || []).map((c) => (typeof c === "string" ? c : c.label || c.code)),
      additional_charges: rate.additional_charges || [],
      source: rate.source,
      ts: Date.now(),
    };
    localStorage.setItem("rate_to_offer", JSON.stringify(payload));
    toast.success("Opening Offer Builder with this rate…");
    navigate("/offers/pricing?from_rate=1");
  };

  const updateRoute = (index, field, value) => {
    const updated = [...routes];
    updated[index][field] = value;
    setRoutes(updated);
  };

  const handleSearch = async () => {
    // Validate
    const mainRoute = routes[0];
    if (!mainRoute.origin || !mainRoute.destination) {
      toast.error("Please enter origin and destination");
      return;
    }

    setLoading(true);
    setResults(null);
    setActiveTab("results");

    try {
      const response = await axios.post(
        `${API_URL}/rate-finder/search`,
        {
          routes: routes,
          origin_type: formData.origin_type,
          destination_type: formData.destination_type,
          valid_from: formData.valid_from,
          container_type: formData.container_type,
          container_quantity: formData.container_quantity,
          weight_per_container: formData.weight_per_container,
          commodity: formData.commodity,
          dangerous_goods: formData.dangerous_goods,
          shipper_owned: formData.shipper_owned,
          send_email_rfq: true, // Always send email RFQ
        },
        { headers: getAuthHeaders() }
      );

      setSearchId(response.data.search_id);
      toast.success("Rate search started! Checking all carriers...");
      
      // Poll for results
      pollForResults(response.data.search_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed");
      setLoading(false);
    }
  };

  const pollForResults = async (searchId) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 seconds max
    
    const poll = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/rate-finder/results/${searchId}`,
          { headers: getAuthHeaders() }
        );
        
        setResults(response.data);
        
        const terminal = ["completed", "partial", "email_only", "error"];
        if (terminal.includes(response.data.status)) {
          setLoading(false);
          fetchSearchHistory();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000);
        } else {
          setLoading(false);
          toast.error("Search timed out. Some carriers may not have responded.");
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000);
        } else {
          setLoading(false);
        }
      }
    };
    
    poll();
  };

  // Container type options
  const containerTypes = [
    { value: "20STD", label: "20' Standard" },
    { value: "40STD", label: "40' Standard" },
    { value: "40HC", label: "40' High Cube" },
    { value: "45HC", label: "45' High Cube" },
    { value: "20RF", label: "20' Reefer" },
    { value: "40RF", label: "40' Reefer" },
  ];

  const commodityTypes = [
    { value: "FAK", label: "FAK - Freight All Kind" },
    { value: "GEN", label: "General Cargo" },
    { value: "FOOD", label: "Foodstuff" },
    { value: "MACH", label: "Machinery" },
    { value: "CHEM", label: "Chemicals" },
    { value: "TEXT", label: "Textiles" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Rate Finder</h1>
              <p className="text-sm text-slate-500">Search rates across all carriers</p>
            </div>
            <div className="flex items-center gap-4">
              {CARRIERS && Object.entries(CARRIERS).map(([key, carrier]) => (
                <div 
                  key={key} 
                  className="flex items-center gap-1 text-xs"
                  style={{ color: carrier.color }}
                >
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: carrier.color }}
                  />
                  {carrier.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="search" className="gap-2">
              <Search className="w-4 h-4" />
              New Search
            </TabsTrigger>
            <TabsTrigger value="results" className="gap-2">
              <FileText className="w-4 h-4" />
              Results
              {results?.rates?.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {results.rates.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <Clock className="w-4 h-4" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              {/* Progress Steps */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">1</div>
                    <span className="font-medium">Search</span>
                  </div>
                  <div className="w-16 h-0.5 bg-slate-200" />
                  <div className="flex items-center gap-2 text-slate-400">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold">2</div>
                    <span>Offer Selection</span>
                  </div>
                  <div className="w-16 h-0.5 bg-slate-200" />
                  <div className="flex items-center gap-2 text-slate-400">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold">3</div>
                    <span>Review</span>
                  </div>
                </div>
              </div>

              {/* Routing Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <h2 className="text-lg font-semibold">Routing</h2>
                </div>

                {routes.map((route, index) => (
                  <div key={index} className="flex items-center gap-4 mb-4">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
                      {index + 1}
                    </div>
                    
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Start Location</Label>
                        <PlaceInput
                          value={route.origin}
                          kind={formData.origin_type === "door" ? "all" : "sea"}
                          placeholder="Port or city (e.g., Shanghai)"
                          data-testid={`rate-origin-${index}`}
                          onSelect={(p) => updateRoute(index, "origin", p.code || p.name || "")}
                        />
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`origin-type-${index}`}
                              checked={formData.origin_type === "door"}
                              onChange={() => setFormData({ ...formData, origin_type: "door" })}
                              className="text-orange-500"
                            />
                            Your Door
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`origin-type-${index}`}
                              checked={formData.origin_type === "terminal"}
                              onChange={() => setFormData({ ...formData, origin_type: "terminal" })}
                              className="text-orange-500"
                            />
                            Terminal/Ramp
                          </label>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">End Location</Label>
                        <PlaceInput
                          value={route.destination}
                          kind={formData.destination_type === "door" ? "all" : "sea"}
                          placeholder="Port or city (e.g., Koper)"
                          data-testid={`rate-destination-${index}`}
                          onSelect={(p) => updateRoute(index, "destination", p.code || p.name || "")}
                        />
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`dest-type-${index}`}
                              checked={formData.destination_type === "door"}
                              onChange={() => setFormData({ ...formData, destination_type: "door" })}
                            />
                            Your Door
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`dest-type-${index}`}
                              checked={formData.destination_type === "terminal"}
                              onChange={() => setFormData({ ...formData, destination_type: "terminal" })}
                            />
                            Terminal/Ramp
                          </label>
                        </div>
                      </div>
                    </div>

                    {routes.length > 1 && (
                      <button
                        onClick={() => removeRoute(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                <Button
                  variant="outline"
                  onClick={addRoute}
                  className="mt-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Routing
                </Button>
              </div>

              {/* Validity Date */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <h2 className="text-lg font-semibold">Validity Date</h2>
                </div>
                <div className="w-48">
                  <Label className="text-xs text-slate-500 mb-1 block">Valid from</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
              </div>

              {/* Container Details */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-slate-400" />
                  <h2 className="text-lg font-semibold">Container and Commodity Details</h2>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.shipper_owned}
                      onChange={(e) => setFormData({ ...formData, shipper_owned: e.target.checked })}
                    />
                    Shipper Owned Container (SOC)
                  </label>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Container Type</Label>
                    <Select
                      value={formData.container_type}
                      onValueChange={(v) => setFormData({ ...formData, container_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {containerTypes.map((ct) => (
                          <SelectItem key={ct.value} value={ct.value}>
                            {ct.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Container Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.container_quantity}
                      onChange={(e) => setFormData({ ...formData, container_quantity: parseInt(e.target.value) || 1 })}
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Weight per Container</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={formData.weight_per_container}
                        onChange={(e) => setFormData({ ...formData, weight_per_container: parseInt(e.target.value) || 0 })}
                      />
                      <span className="text-sm text-slate-500">kg</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Commodity</Label>
                    <Select
                      value={formData.commodity}
                      onValueChange={(v) => setFormData({ ...formData, commodity: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {commodityTypes.map((ct) => (
                          <SelectItem key={ct.value} value={ct.value}>
                            {ct.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.dangerous_goods}
                      onChange={(e) => setFormData({ ...formData, dangerous_goods: e.target.checked })}
                    />
                    Dangerous Goods
                  </label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">Multiple Container Types</p>
                      <p className="text-sm text-blue-600">
                        You will get quotation offers for 20'STD, 40'STD and 40'HC on one search.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex justify-center">
                <Button
                  onClick={handleSearch}
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-12 py-6 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Search All Carriers
                    </>
                  )}
                </Button>
              </div>

              <p className="text-center text-sm text-slate-500 mt-4">
                Will search: Maersk, MSC, CMA CGM, Hapag-Lloyd + send RFQ to local agents
              </p>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Searching Carriers...</h3>
                  <p className="text-slate-500">Checking Maersk, MSC, CMA CGM, Hapag-Lloyd</p>
                  <div className="flex justify-center gap-4 mt-6">
                    {Object.entries(CARRIERS).map(([key, carrier]) => (
                      <div key={key} className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: carrier.color }} />
                        <span className="text-sm">{carrier.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : results ? (
                <div>
                  {/* Route Summary */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-4 text-lg">
                      <span className="font-semibold">{results.search?.origin || routes[0]?.origin}</span>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                      <span className="font-semibold">{results.search?.destination || routes[0]?.destination}</span>
                      <Badge variant="outline" className="ml-auto">
                        {formData.container_type} x {formData.container_quantity}
                      </Badge>
                    </div>
                  </div>

                  {/* Rate Cards */}
                  {results.rates && results.rates.length > 0 ? (
                    <div className="space-y-4">
                      {results.rates
                        .sort((a, b) => (a.rate || 999999) - (b.rate || 999999))
                        .map((rate, index) => (
                        <div
                          key={index}
                          className={`border rounded-xl p-4 ${
                            index === 0 ? "border-green-500 bg-green-50/50" : "border-slate-200"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: CARRIERS[rate.carrier]?.color || "#666" }}
                              >
                                {rate.carrier?.substring(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <h4 className="font-semibold text-lg">
                                  {CARRIERS[rate.carrier]?.name || rate.carrier}
                                </h4>
                                <p className="text-sm text-slate-500">{rate.service_name || "Direct Service"}</p>
                              </div>
                              {index === 0 && (
                                <Badge className="bg-green-500">Best Rate</Badge>
                              )}
                            </div>
                            
                            <div className="text-right">
                              <p className="text-2xl font-bold text-slate-800">
                                ${rate.rate?.toLocaleString() || "N/A"}
                              </p>
                              <p className="text-sm text-slate-500">per container</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
                            <div>
                              <p className="text-xs text-slate-500">Transit Time</p>
                              <p className="font-medium">{rate.transit_days || "N/A"} days</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Departure</p>
                              <p className="font-medium">{rate.departure || "Weekly"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Valid Until</p>
                              <p className="font-medium">{rate.valid_until || "30 days"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Source</p>
                              <Badge variant="outline">{rate.source || "Portal"}</Badge>
                            </div>
                          </div>

                          {rate.rate != null && (rate.included_charges?.length > 0 || rate.additional_charges?.length > 0) && (
                            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid={`rate-charges-${rate.carrier}`}>
                              {rate.included_charges?.length > 0 && (
                                <details>
                                  <summary className="cursor-pointer text-xs font-medium text-emerald-700">✓ Included in this rate ({rate.included_charges.length})</summary>
                                  <ul className="mt-1 text-xs text-slate-500 list-disc pl-5">
                                    {rate.included_charges.map((c, ci) => <li key={ci}>{typeof c === "string" ? c : (c.label || c.code)}</li>)}
                                  </ul>
                                </details>
                              )}
                              {rate.additional_charges?.length > 0 && (
                                <details>
                                  <summary className="cursor-pointer text-xs font-medium text-amber-700">+ Payable at destination ({rate.additional_charges.length})</summary>
                                  <table className="mt-1 w-full text-xs">
                                    <tbody>
                                      {rate.additional_charges.map((s, si) => (
                                        <tr key={si} className="border-t border-slate-100">
                                          <td className="py-1 pr-2 text-slate-600">{s.code}</td>
                                          <td className="py-1 pr-2 text-slate-500">{s.label}</td>
                                          <td className="py-1 text-right font-medium">{s.amount?.toLocaleString?.() || s.amount} {s.currency}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </details>
                              )}
                            </div>
                          )}

                          {rate.rate != null && (
                            <div className="mt-3 flex justify-end">
                              <Button size="sm" variant="outline" data-testid={`build-offer-${rate.carrier}`} onClick={() => buildOfferFromRate(rate)}>
                                <ArrowRight className="w-4 h-4 mr-1.5" /> Build Offer from this rate
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No instant rates available</h3>
                      <p className="text-slate-500 mb-4">
                        RFQ email has been sent to carrier agents. Results will arrive via email.
                      </p>
                      <Badge variant="outline" className="text-green-600">
                        <Mail className="w-4 h-4 mr-1" />
                        RFQ sent to quotedesk.fcl@europacific.com
                      </Badge>
                    </div>
                  )}

                  {/* Email RFQ Status */}
                  {results.email_sent && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-medium text-blue-800">RFQ Email Sent</p>
                          <p className="text-sm text-blue-600">
                            Request sent to local carrier agents. Check inbox for additional quotes.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Start a search to see results</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Searches</h3>
              
              {searchHistory.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No search history yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {searchHistory.map((search) => (
                    <div
                      key={search.id}
                      className="border rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Ship className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium">
                              {search.origin} → {search.destination}
                            </p>
                            <p className="text-sm text-slate-500">
                              {search.container_type} x {search.container_quantity} •{" "}
                              {new Date(search.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={search.rates_found > 0 ? "default" : "secondary"}>
                            {search.rates_found || 0} rates
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
